import {
  GuestEnvironment,
  GuestEnvironmentCapabilities,
  GuestEnvironmentList,
  CreateGuestEnvironment,
  GuestProvisioningGrant,
  GuestEnvironmentAction,
  GuestActionGrant,
} from "./guestEnvironment.ts";
import { ThreadActionSelection, ThreadManagementAction } from "./orchestration.ts";
import * as Schema from "effect/Schema";

import {
  IsoDateTime,
  MessageId,
  NonNegativeInt,
  ProjectId,
  ThreadId,
  TrimmedNonEmptyString,
  TurnId,
} from "./baseSchemas.ts";
import {
  CommandLineDiscoveryProject,
  CommandLineProjectListData,
  CommandLineProjectShowData,
  CommandLinePromptText,
  CommandLineThreadApprovalListData,
  CommandLineThreadInputAnswers,
  CommandLineThreadInputListData,
  CommandLineThreadListData,
  CommandLineThreadOptionsData,
  CommandLineThreadOutputData,
  CommandLineThreadOutputView,
  CommandLineThreadShowData,
  CommandLineThreadStatusData,
  CommandLineWorktreeListData,
} from "./commandLineClient.ts";
import {
  EnvironmentCommandAcceptedWorkIdentity,
  EnvironmentCommandAcceptedReceipt,
  EnvironmentCommandReceipt,
  ProviderApprovalDecision,
} from "./orchestration.ts";
import { ProviderInstanceId } from "./providerInstance.ts";
import {
  WorkflowToolCancelInput,
  WorkflowToolGetRunInput,
  WorkflowToolListResult,
  WorkflowToolPresentInput,
  WorkflowToolPresentResult,
  WorkflowToolRunInput,
  WorkflowToolRunResult,
} from "./workflow.ts";
import { ManagedThreadWorktreeDeleteResult, ManagedThreadWorktreeId } from "./worktrees.ts";

/**
 * A tool that takes no input still has to advertise an object schema. Effect
 * compiles `Schema.Struct({})` to `{"anyOf":[{"type":"object"},{"type":"array"}]}`,
 * which is not a legal MCP `inputSchema`: Claude Code rejects the malformed
 * entry and drops every tool on the server with it, so the whole App Control
 * Tool Surface went missing from Claude sessions while Codex, which is
 * lenient, saw all of them.
 */
const EmptyInput = Schema.Record(Schema.String, Schema.Unknown);
const ProjectInput = Schema.Struct({ projectId: ProjectId });
const ThreadInput = Schema.Struct({ threadId: ThreadId });
const ThreadReceiptResult = Schema.Struct({
  threadId: ThreadId,
  receipt: EnvironmentCommandAcceptedReceipt,
});
const ProjectReceiptResult = Schema.Struct({
  projectId: ProjectId,
  receipt: EnvironmentCommandAcceptedReceipt,
});
const PromptReceiptResult = Schema.Struct({
  threadId: ThreadId,
  messageId: MessageId,
  acceptedWork: Schema.NullOr(EnvironmentCommandAcceptedWorkIdentity),
  receipt: EnvironmentCommandAcceptedReceipt,
});

export const APP_CONTROL_OPERATIONS = [
  "project_list",
  "project_show",
  "project_add",
  "project_create_from_references",
  "project_start_study",
  "project_skip_study",
  "project_remove",
  "project_rename",
  "worktree_list",
  "worktree_delete",
  "thread_list",
  "thread_show",
  "thread_options",
  "thread_status",
  "thread_output",
  "thread_watch",
  "thread_prompt",
  "thread_create",
  "thread_approval_list",
  "thread_approval_respond",
  "thread_input_list",
  "thread_input_respond",
  "thread_fork",
  "thread_revert",
  "thread_manage",
  "thread_archive",
  "thread_unarchive",
  "thread_delete",
  "thread_interrupt",
  "thread_stop",
  "guest_environment_list",
  "guest_environment_create",
  "guest_environment_manage",
  "machine_pool_list",
  "machine_pool_thread_create",
  "machine_pool_thread_status",
  "workflow_list",
  "workflow_present",
  "workflow_run",
  "workflow_get_run",
  "workflow_cancel",
] as const;

export type AppControlOperation = (typeof APP_CONTROL_OPERATIONS)[number];

export const AppControlOperationSchemas = {
  project_list: { input: EmptyInput, output: CommandLineProjectListData },
  project_show: { input: ProjectInput, output: CommandLineProjectShowData },
  project_add: {
    input: Schema.Struct({
      workspaceRoot: TrimmedNonEmptyString,
      title: Schema.optional(TrimmedNonEmptyString),
    }),
    output: Schema.Union([
      Schema.Struct({
        disposition: Schema.Literal("already-present"),
        project: CommandLineDiscoveryProject,
      }),
      Schema.Struct({
        projectId: ProjectId,
        title: TrimmedNonEmptyString,
        workspaceRoot: TrimmedNonEmptyString,
        receipt: EnvironmentCommandAcceptedReceipt,
      }),
    ]),
  },
  project_create_from_references: {
    input: Schema.Struct({
      destinationPath: TrimmedNonEmptyString,
      references: Schema.Array(TrimmedNonEmptyString).check(Schema.isMinLength(1)),
      providerInstanceId: Schema.optional(ProviderInstanceId),
      modelId: Schema.optional(TrimmedNonEmptyString),
    }),
    output: Schema.Struct({
      projectId: ProjectId,
      threadId: ThreadId,
      messageId: MessageId,
      receipt: EnvironmentCommandAcceptedReceipt,
    }),
  },
  project_start_study: {
    input: Schema.Struct({
      projectId: ProjectId,
      study: TrimmedNonEmptyString,
      providerInstanceId: Schema.optional(ProviderInstanceId),
      modelId: Schema.optional(TrimmedNonEmptyString),
    }),
    output: PromptReceiptResult,
  },
  project_skip_study: { input: ProjectInput, output: ProjectReceiptResult },
  project_remove: { input: ProjectInput, output: ProjectReceiptResult },
  project_rename: {
    input: Schema.Struct({ projectId: ProjectId, title: TrimmedNonEmptyString }),
    output: ProjectReceiptResult,
  },
  worktree_list: {
    input: Schema.Struct({ projectId: Schema.optional(ProjectId) }),
    output: CommandLineWorktreeListData,
  },
  worktree_delete: {
    input: Schema.Struct({ projectId: ProjectId, worktreeId: ManagedThreadWorktreeId }),
    output: Schema.Struct({
      deletion: ManagedThreadWorktreeDeleteResult,
      receipt: EnvironmentCommandReceipt,
    }),
  },
  thread_list: {
    input: Schema.Struct({ projectId: Schema.optional(ProjectId) }),
    output: CommandLineThreadListData,
  },
  thread_show: { input: ThreadInput, output: CommandLineThreadShowData },
  thread_options: { input: ProjectInput, output: CommandLineThreadOptionsData },
  thread_status: { input: ThreadInput, output: CommandLineThreadStatusData },
  thread_output: {
    input: Schema.Struct({
      threadId: ThreadId,
      view: Schema.optional(CommandLineThreadOutputView),
      turnId: Schema.optional(TurnId),
    }),
    output: CommandLineThreadOutputData,
  },
  thread_watch: {
    input: Schema.Struct({
      threadId: ThreadId,
      afterSequence: Schema.optional(NonNegativeInt),
      timeoutMs: Schema.optional(NonNegativeInt),
    }),
    output: Schema.Struct({ changed: Schema.Boolean, status: CommandLineThreadStatusData }),
  },
  thread_prompt: {
    input: Schema.Struct({ threadId: ThreadId, prompt: CommandLinePromptText }),
    output: PromptReceiptResult,
  },
  thread_create: {
    input: Schema.Struct({
      projectId: ProjectId,
      prompt: CommandLinePromptText,
      title: Schema.optional(TrimmedNonEmptyString),
      action: Schema.optional(ThreadActionSelection),
      providerInstanceId: Schema.optional(ProviderInstanceId),
      modelId: Schema.optional(TrimmedNonEmptyString),
      effort: Schema.optional(TrimmedNonEmptyString),
      checkoutId: Schema.optional(TrimmedNonEmptyString),
      baseBranch: Schema.optional(TrimmedNonEmptyString),
      newBranch: Schema.optional(TrimmedNonEmptyString),
    }),
    output: PromptReceiptResult,
  },
  thread_approval_list: {
    input: Schema.Struct({ threadId: ThreadId, includeHistory: Schema.optional(Schema.Boolean) }),
    output: CommandLineThreadApprovalListData,
  },
  thread_approval_respond: {
    input: Schema.Struct({
      threadId: ThreadId,
      requestId: TrimmedNonEmptyString,
      decision: ProviderApprovalDecision,
    }),
    output: Schema.Union([
      ThreadReceiptResult,
      Schema.Struct({
        threadId: ThreadId,
        disposition: Schema.Literal("already-authoritative"),
        decision: ProviderApprovalDecision,
      }),
    ]),
  },
  thread_input_list: {
    input: Schema.Struct({ threadId: ThreadId, includeHistory: Schema.optional(Schema.Boolean) }),
    output: CommandLineThreadInputListData,
  },
  thread_input_respond: {
    input: Schema.Struct({
      threadId: ThreadId,
      requestId: TrimmedNonEmptyString,
      answers: CommandLineThreadInputAnswers,
    }),
    output: Schema.Union([
      ThreadReceiptResult,
      Schema.Struct({
        threadId: ThreadId,
        disposition: Schema.Literal("already-authoritative"),
        answeredQuestionIds: Schema.Array(TrimmedNonEmptyString),
      }),
    ]),
  },
  thread_fork: {
    input: Schema.Struct({ threadId: ThreadId, throughTurnId: TurnId }),
    output: Schema.Struct({
      threadId: ThreadId,
      targetThreadId: ThreadId,
      receipt: EnvironmentCommandAcceptedReceipt,
    }),
  },
  thread_revert: {
    input: Schema.Struct({
      threadId: ThreadId,
      turnCount: NonNegativeInt,
      /** Omitted restores the workspace; `false` rewinds history only. */
      restoreFiles: Schema.optional(Schema.Boolean),
    }),
    output: ThreadReceiptResult,
  },
  thread_manage: {
    input: Schema.Struct({
      threadId: ThreadId,
      action: ThreadManagementAction,
      until: Schema.optional(IsoDateTime),
    }),
    output: ThreadReceiptResult,
  },
  thread_archive: { input: ThreadInput, output: ThreadReceiptResult },
  thread_unarchive: { input: ThreadInput, output: ThreadReceiptResult },
  thread_delete: { input: ThreadInput, output: ThreadReceiptResult },
  thread_interrupt: { input: ThreadInput, output: ThreadReceiptResult },
  thread_stop: { input: ThreadInput, output: ThreadReceiptResult },
  // A project's Machine Pool (ADR-0149): the other Environments this thread's project may start
  // threads on. The receipt and status are the destination's own, passed through.
  guest_environment_list: {
    input: EmptyInput,
    output: Schema.Struct({
      capabilities: GuestEnvironmentCapabilities,
      ...GuestEnvironmentList.fields,
    }),
  },
  guest_environment_create: {
    input: Schema.Struct({ input: CreateGuestEnvironment, grant: GuestProvisioningGrant }),
    output: GuestEnvironment,
  },
  guest_environment_manage: {
    input: Schema.Struct({ input: GuestEnvironmentAction, grant: GuestActionGrant }),
    output: GuestEnvironment,
  },
  machine_pool_list: {
    input: EmptyInput,
    output: Schema.Struct({
      environments: Schema.Array(
        Schema.Struct({
          environmentId: Schema.String,
          label: Schema.String,
          projectId: Schema.String,
          reachable: Schema.Boolean,
          resources: Schema.NullOr(
            Schema.Struct({
              cpuUtilization: Schema.NullOr(Schema.Number),
              cpuCount: Schema.Number,
              availableMemoryBytes: Schema.Number,
              totalMemoryBytes: Schema.Number,
            }),
          ),
        }),
      ),
    }),
  },
  machine_pool_thread_create: {
    input: Schema.Struct({
      environmentId: TrimmedNonEmptyString,
      prompt: CommandLinePromptText,
      title: Schema.optional(TrimmedNonEmptyString),
    }),
    output: Schema.Struct({ environmentId: Schema.String, receipt: Schema.Unknown }),
  },
  machine_pool_thread_status: {
    input: Schema.Struct({ environmentId: TrimmedNonEmptyString, threadId: ThreadId }),
    output: Schema.Struct({ environmentId: Schema.String, status: Schema.Unknown }),
  },
  workflow_list: { input: EmptyInput, output: WorkflowToolListResult },
  workflow_present: { input: WorkflowToolPresentInput, output: WorkflowToolPresentResult },
  workflow_run: { input: WorkflowToolRunInput, output: WorkflowToolRunResult },
  workflow_get_run: { input: WorkflowToolGetRunInput, output: WorkflowToolRunResult },
  workflow_cancel: { input: WorkflowToolCancelInput, output: WorkflowToolRunResult },
} as const satisfies Record<
  AppControlOperation,
  { readonly input: Schema.Top; readonly output: Schema.Top }
>;

export type AppControlInput<Operation extends AppControlOperation> = Schema.Schema.Type<
  (typeof AppControlOperationSchemas)[Operation]["input"]
>;

export type AppControlOutput<Operation extends AppControlOperation> = Schema.Schema.Type<
  (typeof AppControlOperationSchemas)[Operation]["output"]
>;
